'use client';

import Link from 'next/link';
import Image from 'next/image';
import type { StaticImageData } from 'next/image';
import { LayoutIcon } from './icons';
import { useHoverTip } from './HoverTip';
import styles from './SidebarHeader.module.css';

interface SidebarHeaderProps {
	expanded: boolean;
	sidebarHovered: boolean;
	compactLogo: string | StaticImageData;
	extendedLogo: string | StaticImageData;
	onToggleExpand: () => void;
}

export function SidebarHeader({
	expanded,
	sidebarHovered,
	compactLogo,
	extendedLogo,
	onToggleExpand,
}: SidebarHeaderProps) {
	const showExpandCue = !expanded && sidebarHovered;

	// Un solo hook para los dos botones: nunca se pintan juntos —`expanded` decide
	// cuál—, así que no hay riesgo de dos globos a la vez. Y a diferencia del menú,
	// éste habla en los DOS estados: el botón es un icono suelto tanto colapsado como
	// desplegado, así que nunca hay texto al lado que lo explique.
	const toggleTip = useHoverTip(expanded ? 'Contraer barra' : 'Expandir barra', 'right');

	return (
		<div
			className={`${styles.sidebarHeaderRoot} ${expanded
				? styles.sidebarHeaderExpanded
				: styles.sidebarHeaderCollapsed
				}`}
		>
			{expanded ? (
				<Link
					href="/"
					className={styles.sidebarBrandLink}
					aria-label="Ir al inicio de InvestigaUDA"
				>
					<Image
						src={extendedLogo}
						alt="Universidad de Atacama"
						className={styles.sidebarExtendedLogo}
						priority
						loading="eager"
						unoptimized
					/>
				</Link>
			) : (
				<button
					type="button"
					onClick={onToggleExpand}
					data-tutorial="home-sidebar-toggle"
					className={styles.sidebarCollapsedButton}
					aria-label="Expandir barra"
					{...toggleTip.handlers}
				>
					{toggleTip.tip}
					<span className={styles.sidebarCollapsedVisual}>
						<span
							className={`${styles.sidebarCollapsedLayer} ${showExpandCue
								? styles.sidebarCollapsedLayerCue
								: ''
								} ${showExpandCue
									? styles.sidebarCollapsedLayerHidden
									: styles.sidebarCollapsedLayerVisible
								}`}
						>
							<Image
								src={compactLogo}
								alt="Universidad de Atacama"
								width={64}
								height={64}
								className={styles.sidebarCompactLogo}
								priority
								loading="eager"
								unoptimized
							/>
						</span>
						<span
							className={`${styles.sidebarCollapsedLayer} ${showExpandCue
								? styles.sidebarCollapsedLayerCue
								: ''
								} ${showExpandCue
									? styles.sidebarCollapsedLayerVisible
									: styles.sidebarCollapsedLayerHidden
								}`}
						>
							<LayoutIcon
								name="panel-left-open"
								size={36}
								className={`${styles.sidebarCollapsedOpenIcon} ${showExpandCue ? styles.sidebarCollapsedOpenIconCue : ''
									}`}
							/>
						</span>
					</span>
				</button>
			)}

			{expanded && (
				<button
					type="button"
					onClick={onToggleExpand}
					data-tutorial="home-sidebar-toggle"
					className={`${styles.sidebarHeaderToggle} ${styles.sidebarHeaderToggleExpanded
						}`}
					aria-label="Contraer barra"
					{...toggleTip.handlers}
				>
					<LayoutIcon
						name="panel-left-close"
						size={40}
						className={styles.sidebarHeaderToggleIcon}
					/>
					{toggleTip.tip}
				</button>
			)}
		</div>
	);
}
